import React, { useState, useEffect } from "react";
import { ChevronDown, Shield, HelpCircle } from "lucide-react";
import { useTheme } from "../utils/theme";
import { SimpleTooltip } from "./design-system";

// Tooltip definitions
const tooltipDefinitions = {
  SPG: "Average number of steals per game by the player.",
  BPG: "Average number of blocks per game by the player.",
  RPG: "Average number of rebounds per game by the player.",
  APG: "Average number of assists per game by the player.",
  "Steal Rate":
    "A player's predicted steal percentage against an average opponent. Steal percentage is percentage of defensive possessions that end in the player stealing the ball while on the court.",
  "Block Rate":
    "A player's predicted block percentage against an average opponent, adjusted for usage. Block percentage is the percentage of opponent two-point field goal attempts that the player blocked while on the court.",
  "Personal Fouls":
    "A player's predicted personal fouls committed per 100 possessions.",
  "Defensive BPR":
    "A player's defensive per-possession value to a team, against an average opponent, as measured by Defensive BPR. DBPR reflects the defensive value a player brings to his team when he is on the court.",
  "Offensive Rebound %":
    "A player's predicted offensive rebound percentage against an average opponent, adjusted for usage. Offensive rebound percentage is the percentage of possible offensive rebounds that the player secured while on the court.",
  "Defensive Rebound %":
    "A player's predicted defensive rebound percentage against an average opponent, adjusted for usage. Defensive rebound percentage is the percentage of possible defensive rebounds that the player secured while on the court.",
  "Total Rebound %":
    "A player's predicted offensive + defensive rebounding rate. See O-Reb % and D-Reb % for more detail.",
};

// Status badge colors and styles - updated to match document rules
const statusStyles = {
  GOOD: { backgroundColor: "#bbf7d0", color: "#16a34a" },
  AVERAGE: { backgroundColor: "#fef3c7", color: "#d97706" },
  POOR: { backgroundColor: "#fecaca", color: "#dc2626" },
  DISCIPLINED: { backgroundColor: "#bbf7d0", color: "#16a34a" },
  "FOUL TROUBLE": { backgroundColor: "#fecaca", color: "#dc2626" },
  "NEEDS WORK": { backgroundColor: "#fecaca", color: "#dc2626" },
  "BELOW AVERAGE": { backgroundColor: "#fed7aa", color: "#ea580c" },
  WEAK: { backgroundColor: "#fed7aa", color: "#ea580c" },
  COMBINED: { backgroundColor: "#fef3c7", color: "#d97706" },
  "NEGATIVE IMPACT": { backgroundColor: "#fecaca", color: "#dc2626" },
  "LIMITED IMPACT": { backgroundColor: "#fed7aa", color: "#ea580c" },
};

const DefensiveTab = ({ stats }) => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [season, setSeason] = useState("2023 - 2024 Season");
  const [opponents, setOpponents] = useState("All Opponents");
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  const [opponentsDropdownOpen, setOpponentsDropdownOpen] = useState(false);
  // Removed custom tooltip states as we're using antd Tooltip

  const seasonOptions = [
    "2023 - 2024 Season",
    "2022 - 2023 Season",
    "2021 - 2022 Season",
  ];
  const opponentsOptions = [
    "All Opponents",
    "Conference Only",
    "Non-Conference",
    "Ranked Teams",
    "Home Games",
    "Away Games",
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setSeasonDropdownOpen(false);
      setOpponentsDropdownOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Removed scroll event listener for custom tooltip

  // Function to determine steal rate status
  const getStealRateStatus = (value) => {
    if (!value) return null;
    if (value < 1.5) return "POOR";
    if (value >= 1.5 && value <= 2.5) return "AVERAGE";
    if (value > 2.5) return "GOOD";
    return null;
  };

  // Function to determine block rate status (assuming position, defaulting to guard)
  const getBlockRateStatus = (value, position = "guard") => {
    if (!value) return null;
    if (value < 2) return "POOR";

    switch (position.toLowerCase()) {
      case "guard":
        return value > 1.5 ? "GOOD" : "POOR";
      case "wing":
        return value > 2.5 ? "GOOD" : "POOR";
      case "big":
        return value > 4 ? "GOOD" : "POOR";
      default:
        return value > 1.5 ? "GOOD" : "POOR";
    }
  };

  // Function to determine SPG status
  const getOverallSPGStatus = (value) => {
    if (!value) return null;
    if (value < 0.5) return "BELOW AVERAGE";
    if (value >= 0.5 && value <= 1.0) return "AVERAGE";
    if (value > 1.0 && value <= 1.5) return "GOOD";
    return "ABOVE AVERAGE"; // >1.5
  };

  // Function to determine BPG status
  const getOverallBPGStatus = (value) => {
    if (!value) return null;
    if (value < 0.3) return "BELOW AVERAGE";
    if (value >= 0.3 && value <= 0.8) return "AVERAGE";
    if (value > 0.8 && value <= 1.5) return "GOOD";
    return "ABOVE AVERAGE"; // >1.5
  };

  // Function to determine RPG status
  const getOverallRPGStatus = (value) => {
    if (!value) return null;
    if (value < 3) return "BELOW AVERAGE";
    if (value >= 3 && value <= 6) return "AVERAGE";
    if (value > 6 && value <= 9) return "GOOD";
    return "ABOVE AVERAGE"; // >9
  };

  // Function to determine APG status
  const getOverallAPGStatus = (value) => {
    if (!value) return null;
    if (value < 2) return "BELOW AVERAGE";
    if (value >= 2 && value <= 4) return "AVERAGE";
    if (value > 4 && value <= 6) return "GOOD";
    return "ABOVE AVERAGE"; // >6
  };

  // Function to determine personal fouls status
  const getPersonalFoulsStatus = (value) => {
    if (!value) return null;
    if (value > 5) return "FOUL TROUBLE";
    if (value >= 3 && value <= 5) return "AVERAGE";
    if (value < 3) return "DISCIPLINED";
    return null;
  };

  // Function to determine defensive BPR status
  const getDefensiveBPRStatus = (value) => {
    if (!value && value !== 0) return null;
    if (value > 0) return "NEEDS WORK";
    if (value >= -0.5 && value <= 0) return "AVERAGE";
    if (value < -0.5) return "GOOD";
    return null;
  };

  // Function to determine offensive rebound % status (assuming position, defaulting to guard)
  const getOffensiveReboundStatus = (value, position = "guard") => {
    if (!value) return null;

    switch (position.toLowerCase()) {
      case "big":
        if (value > 12) return "GOOD";
        if (value < 8) return "POOR";
        return "AVERAGE";
      case "guard":
      default:
        if (value > 3) return "GOOD";
        if (value < 1) return "POOR";
        return "AVERAGE";
    }
  };

  // Function to determine defensive rebound % status (assuming position, defaulting to guard)
  const getDefensiveReboundStatus = (value, position = "guard") => {
    if (!value) return null;

    switch (position.toLowerCase()) {
      case "big":
        return value > 20 ? "GOOD" : "AVERAGE";
      case "guard":
      default:
        if (value > 15) return "GOOD";
        if (value < 10) return "POOR";
        return "AVERAGE";
    }
  };

  // Value colors matching status colors
  const getValueColor = (status) => {
    if (!status) return themeStyles.textPrimary;
    return statusStyles[status]?.color || themeStyles.textPrimary;
  };

  // Removed custom tooltip show handler

  // Removed custom tooltip hide handler

  const themeStyles = {
    background: "#0f172a",
    cardBackground: "#1e293b",
    textPrimary: "#f1f5f9",
    textSecondary: "#94a3b8",
    border: "#334155",
    progressBackground: "#334155",
    selectBorder: "#334155",
    selectHover: "#475569",
  };

  // Removed custom Tooltip component in favor of antd Tooltip

  const DefensiveCard = ({
    title,
    value,
    subtitle,
    status,
    isShowBar = true,
  }) => (
    <div
      style={{
        backgroundColor: themeStyles.cardBackground,
        borderRadius: "8px",
        padding: "20px",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
        border: `1px solid ${themeStyles.border}`,
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="text-sm font-medium"
          style={{
            color: themeStyles.textSecondary,
            letterSpacing: "0.02em",
          }}
        >
          {title}
        </span>
        {tooltipDefinitions[title] && (
          <SimpleTooltip title={tooltipDefinitions[title]}>
            <HelpCircle
              size={13}
              style={{
                color: themeStyles.textSecondary,
                opacity: 0.7,
                cursor: "help",
              }}
            />
          </SimpleTooltip>
        )}
      </div>

      <div
        style={{
          color: getValueColor(status),
          fontSize: "24px",
          fontWeight: "700",
          lineHeight: "1",
          marginBottom: "8px",
        }}
      >
        {value}
      </div>

      {subtitle && (
        <div
          style={{
            color: themeStyles.textSecondary,
            fontSize: "12px",
            marginBottom: "16px",
          }}
        >
          {subtitle}
        </div>
      )}

      {status && (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              display: "inline-block",
              ...statusStyles[status],
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: "600",
              textAlign: "center",
              letterSpacing: "0.025em",
            }}
          >
            {status}
          </div>
        </div>
      )}

      {isShowBar ? (
        <div
          style={{
            width: "100%",
            height: "4px",
            backgroundColor: themeStyles.progressBackground,
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor:
                status && statusStyles[status]
                  ? statusStyles[status].color
                  : "#ffd700",
              borderRadius: "2px",
            }}
          />
        </div>
      ) : null}
    </div>
  );

  const SectionTitle = ({ children }) => (
    <h2
      style={{
        color: themeStyles.textPrimary,
        fontSize: "18px",
        fontWeight: "600",
        margin: "5px 0 10px 0",
        letterSpacing: "-0.01em",
      }}
    >
      {children}
    </h2>
  );

  const SimpleSelect = ({ value, options, onChange, isOpen, setIsOpen }) => (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: themeStyles.cardBackground,
          border: `1px solid ${themeStyles.selectBorder}`,
          borderRadius: "4px",
          padding: "8px 32px 8px 12px",
          fontSize: "14px",
          color: themeStyles.textPrimary,
          fontWeight: "400",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minWidth: "160px",
          cursor: "pointer",
          transition: "border-color 0.2s ease",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          if (!isOpen) e.target.style.borderColor = themeStyles.selectHover;
        }}
        onMouseLeave={(e) => {
          if (!isOpen) e.target.style.borderColor = themeStyles.selectBorder;
        }}
      >
        <span>{value}</span>
        <ChevronDown
          size={14}
          style={{
            color: themeStyles.textSecondary,
            position: "absolute",
            right: "8px",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      {isOpen && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setIsOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              backgroundColor: themeStyles.cardBackground,
              border: `1px solid ${themeStyles.border}`,
              borderRadius: "4px",
              marginTop: "2px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.4)",
              zIndex: 1000,
              overflow: "hidden",
            }}
          >
            {options.map((option, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(option);
                  setIsOpen(false);
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontSize: "14px",
                  color: themeStyles.textPrimary,
                  backgroundColor: "transparent",
                  border: "none",
                  textAlign: "left",
                  cursor: "pointer",
                  fontWeight: "400",
                  transition: "background-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#334155";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: themeStyles.background,
      }}
    >
      {/* Removed custom tooltip */}
      <div style={{ margin: "0 auto" }}>
        {/* Basic Statistics */}
        <SectionTitle>Overall Metrics </SectionTitle>
        <div className="grid grid-cols-2 gap-6 mb-4 md:grid-cols-4">
          <DefensiveCard
            title="SPG"
            value={
              stats?.steals_per_game ? stats.steals_per_game.toString() : "N/A"
            }
            subtitle="Steals Per Game"
            isShowBar={true}
            status={getOverallSPGStatus(stats?.steals_per_game)}
          />
          <DefensiveCard
            title="BPG"
            value={
              stats?.blocks_per_game ? stats.blocks_per_game.toString() : "N/A"
            }
            subtitle="Blocks Per Game"
            isShowBar={true}
            status={getOverallBPGStatus(stats?.blocks_per_game)}
          />
          <DefensiveCard
            title="RPG"
            value={
              stats?.rebounds_per_game
                ? stats.rebounds_per_game.toString()
                : "N/A"
            }
            subtitle="Rebounds Per Game"
            isShowBar={true}
            status={getOverallRPGStatus(stats?.rebounds_per_game)}

          />
          <DefensiveCard
            title="APG"
            value={
              stats?.assists_per_game
                ? stats.assists_per_game.toString()
                : "N/A"
            }
            subtitle="Assists Per Game"
            isShowBar={true}
            status={getOverallAPGStatus(stats?.assists_per_game)}

          />
        </div>

        {/* Defensive Performance */}
        <SectionTitle>Defensive Performance</SectionTitle>
        <div className="grid grid-cols-1 gap-6 mb-4 md:grid-cols-4">
          <DefensiveCard
            title="Steal Rate"
            value={stats?.stealRate ? `${stats.stealRate}%` : "N/A"}
            subtitle="Possessions ending in steal"
            status={getStealRateStatus(stats?.stealRate)}
          />
          <DefensiveCard
            title="Block Rate"
            value={stats?.blockPct ? `${stats.blockPct}%` : "N/A"}
            subtitle="Blocks per possession"
            status={getBlockRateStatus(stats?.blockPct, stats?.position)}
          />
          <DefensiveCard
            title="Personal Fouls"
            value={
              stats?.personalFouls ? stats.personalFouls.toString() : "N/A"
            }
            subtitle="Per 40 minutes"
            status={getPersonalFoulsStatus(stats?.personalFouls)}
          />
          <DefensiveCard
            title="Defensive BPR"
            value={
              stats?.dbprPredicted ? stats.dbprPredicted.toString() : "N/A"
            }
            subtitle="Points allowed above average"
            status={getDefensiveBPRStatus(stats?.dbprPredicted)}
          />
        </div>

        {/* Rebounding Impact */}
        <SectionTitle>Rebounding Impact</SectionTitle>
        <div className="grid grid-cols-1 gap-6 mb-4 md:grid-cols-3">
          <DefensiveCard
            title="Offensive Rebound %"
            value={stats?.orebPct ? `${stats.orebPct}%` : "N/A"}
            subtitle="Offensive rebounds secured"
            status={getOffensiveReboundStatus(stats?.orebPct, stats?.position)}
          />
          <DefensiveCard
            title="Defensive Rebound %"
            value={stats?.drebPct ? `${stats.drebPct}%` : "N/A"}
            subtitle="Defensive rebounds secured"
            status={getDefensiveReboundStatus(stats?.drebPct, stats?.position)}
          />
          <DefensiveCard
            title="Total Rebound %"
            value={stats?.rebPct ? `${stats.rebPct}%` : "N/A"}
            subtitle="Combined rebounding rate"
            status="COMBINED"
          />
        </div>

        {/* Team Defensive Impact */}
        {/* <SectionTitle>Team Defensive Impact</SectionTitle>
                <div className="grid grid-cols-1 gap-6 mb-4 md:grid-cols-3">
                    <DefensiveCard
                        title="Team Def Efficiency"
                        value={stats?.teamDefEff ? stats.teamDefEff.toString() : "N/A"}
                        subtitle="Points allowed per 100"
                        status="BELOW AVERAGE"
                    />
                    <DefensiveCard
                        title="Opponent FG% Impact"
                        value="N/A"
                        subtitle="When on court"
                        status="NEGATIVE IMPACT"
                    />
                    <DefensiveCard
                        title="Defensive Win Shares"
                        value="N/A"
                        subtitle="Season total"
                        status="LIMITED IMPACT"
                    />
                </div> */}
      </div>
    </div>
  );
};

export default DefensiveTab;
